import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { browseCategoriesForDropdown, formatNameWithCount } from '../categoryLabels';
import { SEARCH_RETAILER_FILTER_ENABLED } from '../constants/searchFilters';

const fmt = new Intl.NumberFormat('mt-MT', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 2,
});

const FILTER_PANEL_MOBILE_MQ = '(max-width: 768px)';
const FILTER_PANEL_VIEWPORT_MARGIN = 12;
const FILTER_PANEL_GAP = 6;

function clamp(n, lo, hi) {
  return Math.min(Math.max(n, lo), hi);
}

function resetFilterPanelPosition(panel) {
  if (!panel) {
    return;
  }

  panel.style.position = '';
  panel.style.top = '';
  panel.style.left = '';
  panel.style.right = '';
  panel.style.width = '';
  panel.style.minWidth = '';
  panel.style.maxWidth = '';
  panel.style.maxHeight = '';
}

function alignFilterPanelToViewport(trigger, panel) {
  if (!trigger || !panel) {
    return;
  }

  resetFilterPanelPosition(panel);

  if (!window.matchMedia(FILTER_PANEL_MOBILE_MQ).matches) {
    return;
  }

  const margin = FILTER_PANEL_VIEWPORT_MARGIN;
  const triggerRect = trigger.getBoundingClientRect();

  panel.style.position = 'fixed';
  panel.style.left = `${margin}px`;
  panel.style.right = `${margin}px`;
  panel.style.width = 'auto';
  panel.style.minWidth = '0';
  panel.style.maxWidth = 'none';
  panel.style.top = `${triggerRect.bottom + FILTER_PANEL_GAP}px`;

  const panelRect = panel.getBoundingClientRect();
  const maxBottom = window.innerHeight - margin;

  if (panelRect.bottom > maxBottom) {
    const spaceBelow = maxBottom - triggerRect.bottom - FILTER_PANEL_GAP;
    const spaceAbove = triggerRect.top - margin - FILTER_PANEL_GAP;

    if (spaceAbove > spaceBelow && spaceAbove >= 160) {
      panel.style.top = `${Math.max(margin, triggerRect.top - FILTER_PANEL_GAP - panelRect.height)}px`;
    } else {
      panel.style.maxHeight = `${Math.max(120, spaceBelow)}px`;
    }
  }
}

function roundMoney(n) {
  return Math.round(n * 100) / 100;
}

function scrollPanelToElement(panel, element) {
  if (!panel || !element) {
    return;
  }
  const panelRect = panel.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const relativeTop = elementRect.top - panelRect.top + panel.scrollTop;
  const padding = 12;
  const maxScroll = Math.max(0, panel.scrollHeight - panel.clientHeight);
  panel.scrollTop = clamp(relativeTop - padding, 0, maxScroll);
}

export function groupRetailersByCategory(retailers, browseCategories) {
  const retailerSet = new Set(retailers);
  const assigned = new Set();
  const groups = [];

  for (const category of browseCategories) {
    if (category.comingSoon) {
      continue;
    }
    const names = (category.retailers ?? [])
      .filter((name) => retailerSet.has(name))
      .sort((a, b) => a.localeCompare(b));
    if (names.length === 0) {
      continue;
    }
    names.forEach((name) => assigned.add(name));
    groups.push({ categoryName: category.name, retailers: names });
  }

  const unassigned = retailers
    .filter((name) => !assigned.has(name))
    .sort((a, b) => a.localeCompare(b));
  if (unassigned.length > 0) {
    groups.push({ categoryName: 'Other', retailers: unassigned });
  }

  return groups;
}

export function parseScopedSearchQuery(
  effectiveQuery,
  browseCategories,
  shopSlug,
) {
  if (!shopSlug) {
    return { userQuery: effectiveQuery, subcategory: null };
  }
  const category = browseCategories.find((c) => c.slug === shopSlug);
  if (!category) {
    return { userQuery: effectiveQuery, subcategory: null };
  }
  const subcategories = Array.isArray(category.subcategories)
    ? category.subcategories
    : [];
  const trimmed = effectiveQuery.trim();

  for (const sub of subcategories) {
    const subSearch = sub.search.trim();
    if (trimmed === subSearch) {
      return { userQuery: '', subcategory: sub };
    }
    const andSuffix = ` AND ${subSearch}`;
    if (trimmed.endsWith(andSuffix)) {
      return {
        userQuery: trimmed.slice(0, trimmed.length - andSuffix.length),
        subcategory: sub,
      };
    }
  }

  return { userQuery: effectiveQuery, subcategory: null };
}

export function findActiveSubcategory(browseCategories, shopSlug, query) {
  return parseScopedSearchQuery(query, browseCategories, shopSlug).subcategory;
}

/**
 * Text for the search input. Strips scoped subcategory suffix when recognized.
 * Returns undefined while a shop-scoped query is still ambiguous (categories loading).
 *
 * @param {string} effectiveQuery
 * @param {BrowseCategory[]} browseCategories
 * @param {string} shopSlug
 * @param {{ search?: string } | null | undefined} [subcategoryHint]
 * @returns {string | undefined}
 */
export function resolveSearchBoxQuery(
  effectiveQuery,
  browseCategories,
  shopSlug,
  subcategoryHint = null,
) {
  const parsed = parseScopedSearchQuery(effectiveQuery, browseCategories, shopSlug);
  if (parsed.subcategory) {
    return parsed.userQuery;
  }

  const hintSearch =
    typeof subcategoryHint?.search === 'string' ? subcategoryHint.search.trim() : '';
  if (hintSearch !== '') {
    const trimmed = effectiveQuery.trim();
    if (trimmed === hintSearch) {
      return '';
    }
    const andSuffix = ` AND ${hintSearch}`;
    if (trimmed.endsWith(andSuffix)) {
      return trimmed.slice(0, trimmed.length - andSuffix.length);
    }
  }

  if (shopSlug && browseCategories.length === 0 && effectiveQuery.includes(' AND ')) {
    return undefined;
  }

  return parsed.userQuery;
}

function CategoryFilterGroup({
  category,
  activeShopCategorySlug,
  activeSubcategorySearch,
  expanded,
  onToggleExpanded,
  onShopCategoryChange,
  onSubcategoryChange,
  onClose,
  scrollTargetRef,
  showCounts = true,
}) {
  const subcategories = Array.isArray(category.subcategories)
    ? category.subcategories
    : [];
  const categorySelected =
    activeShopCategorySlug === category.slug && activeSubcategorySearch === null;
  const isActiveCategory = activeShopCategorySlug === category.slug;
  const hasSubcategories = subcategories.length > 0 && !category.comingSoon;
  const showSubcategories = hasSubcategories && expanded;

  return (
    <li
      className="filter-check-list__group"
      ref={
        isActiveCategory && activeSubcategorySearch === null
          ? scrollTargetRef
          : undefined
      }
    >
      <div className="category-filter-group__header">
        {hasSubcategories && (
          <button
            type="button"
            className="category-filter-group__toggle"
            aria-expanded={showSubcategories}
            aria-label={`${showSubcategories ? 'Collapse' : 'Expand'} ${category.name} subcategories`}
            onClick={onToggleExpanded}
          >
            <CategoryExpandIcon expanded={showSubcategories} />
          </button>
        )}
        <button
          type="button"
          className={`category-breadcrumb-option category-breadcrumb-option--category${
            categorySelected ? ' category-breadcrumb-option--active' : ''
          }${category.comingSoon ? ' category-breadcrumb-option--disabled' : ''}`}
          disabled={category.comingSoon}
          aria-current={categorySelected ? 'true' : undefined}
          onClick={() => {
            if (category.comingSoon) {
              return;
            }
            onShopCategoryChange(category.slug);
            onClose();
          }}
        >
          {showCounts
            ? formatNameWithCount(category.name, category.count)
            : category.name}
        </button>
      </div>
      {showSubcategories && (
        <ul className="filter-check-list__sub-list">
          {subcategories.map((sub) => {
            const subSelected =
              activeShopCategorySlug === category.slug
              && activeSubcategorySearch === sub.search;

            return (
              <li
                key={sub.name}
                className="filter-check-list__sub-item"
                ref={
                  isActiveCategory && activeSubcategorySearch === sub.search
                    ? scrollTargetRef
                    : undefined
                }
              >
                <button
                  type="button"
                  className={`category-breadcrumb-option category-breadcrumb-option--sub${
                    subSelected ? ' category-breadcrumb-option--active' : ''
                  }`}
                  aria-current={subSelected ? 'true' : undefined}
                  onClick={() => {
                    onSubcategoryChange(category.slug, sub.search);
                    onClose();
                  }}
                >
                  {showCounts
                    ? formatNameWithCount(sub.name, sub.count)
                    : sub.name}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

function CategoryFilterPanel({
  panelRef,
  scrollTargetRef,
  dropdownCategories,
  activeShopCategorySlug,
  activeSubcategorySearch,
  expandedCategorySlugs,
  onToggleExpanded,
  onShopCategoryChange,
  onSubcategoryChange,
  onClose,
  className = '',
  ariaLabel = 'Browse categories',
  showCounts = true,
}) {
  return (
    <div
      ref={panelRef}
      className={`filter-pill__panel filter-pill__panel--scroll filter-pill__panel--category${className ? ` ${className}` : ''}`}
      role="dialog"
      aria-label={ariaLabel}
    >
      <ul className="filter-check-list filter-check-list--stack">
        {dropdownCategories.map((c) => (
          <CategoryFilterGroup
            key={c.slug}
            category={c}
            activeShopCategorySlug={activeShopCategorySlug}
            activeSubcategorySearch={activeSubcategorySearch}
            expanded={expandedCategorySlugs.has(c.slug)}
            scrollTargetRef={scrollTargetRef}
            onToggleExpanded={() => onToggleExpanded(c.slug)}
            onShopCategoryChange={onShopCategoryChange}
            onSubcategoryChange={onSubcategoryChange}
            onClose={onClose}
            showCounts={showCounts}
          />
        ))}
      </ul>
    </div>
  );
}

function BreadcrumbSep() {
  return (
    <span className="category-breadcrumbs__sep" aria-hidden="true">
      ›
    </span>
  );
}

function CategoryExpandIcon({ expanded }) {
  return (
    <span className="category-filter-group__toggle-icon" aria-hidden="true">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        {expanded ? (
          <path
            d="M5 7.5L10 12.5L15 7.5"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <path
            d="M7.5 5L12.5 10L7.5 15"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
    </span>
  );
}

function FilterCheckboxPill({ checked, onChange, children }) {
  return (
    <label
      className={`filter-checkbox-pill${checked ? ' filter-checkbox-pill--active' : ''}`}
    >
      <input
        type="checkbox"
        className="filter-checkbox-pill__input"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="filter-checkbox-pill__check" aria-hidden="true">
        {checked && (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M2.5 6L5 8.5L9.5 3.5"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
      <span className="filter-checkbox-pill__text">{children}</span>
    </label>
  );
}

export function FilterViewToggle({ viewMode, onViewModeChange, className = '' }) {
  return (
    <div
      className={`filter-view-toggle${className ? ` ${className}` : ''}`}
      role="group"
      aria-label="Product layout"
    >
      <button
        type="button"
        className={`filter-view-toggle__btn${viewMode === 'grid' ? ' filter-view-toggle__btn--active' : ''}`}
        onClick={() => onViewModeChange('grid')}
        aria-pressed={viewMode === 'grid'}
        aria-label="Grid view"
      >
        <span className="filter-view-toggle__icon" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M1 1h6v6H1V1zm8 0h6v6H9V1zM1 9h6v6H1V9zm8 0h6v6H9V9z" />
          </svg>
        </span>
        <span className="filter-view-toggle__label">Grid</span>
      </button>
      <button
        type="button"
        className={`filter-view-toggle__btn${viewMode === 'list' ? ' filter-view-toggle__btn--active' : ''}`}
        onClick={() => onViewModeChange('list')}
        aria-pressed={viewMode === 'list'}
        aria-label="List view"
      >
        <span className="filter-view-toggle__icon" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M2 3h12v1.5H2V3zm0 4h12v1.5H2V7zm0 4h12v1.5H2V11z" />
          </svg>
        </span>
        <span className="filter-view-toggle__label">List</span>
      </button>
    </div>
  );
}

export function SearchFiltersBar({
  browseCategories = [],
  activeShopCategorySlug,
  activeQuery = '',
  onShopCategoryChange,
  onSubcategoryChange,
  onShopCategoryFilterChange,
  onSubcategoryFilterChange,
  domainMin,
  domainMax,
  filterMin,
  filterMax,
  onFilterMinChange,
  onFilterMaxChange,
  retailers,
  selectedRetailers,
  onToggleRetailer,
  onlyInStock,
  onOnlyInStockChange,
  viewMode,
  onViewModeChange,
  saleActive = false,
  onSaleChange,
}) {
  const [openId, setOpenId] = useState(null);
  const [expandedCategorySlugs, setExpandedCategorySlugs] = useState(
    () => new Set(),
  );
  const [sliderDraft, setSliderDraft] = useState(null);
  const sliderDraftRef = useRef(null);
  const rootRef = useRef(null);
  const categoryPanelRef = useRef(null);
  const categoryBreadcrumbTriggerRef = useRef(null);
  const categoryPillTriggerRef = useRef(null);
  const retailerTriggerRef = useRef(null);
  const retailerPanelRef = useRef(null);
  const scrollTargetRef = useRef(null);

  useEffect(() => {
    sliderDraftRef.current = sliderDraft;
  }, [sliderDraft]);

  useEffect(() => {
    setSliderDraft(null);
  }, [filterMin, filterMax]);

  useEffect(() => {
    if (sliderDraft == null) {
      return undefined;
    }
    function commitSliderDraft() {
      const draft = sliderDraftRef.current;
      if (!draft) {
        return;
      }
      onFilterMinChange(draft.min);
      onFilterMaxChange(draft.max);
      setSliderDraft(null);
    }
    document.addEventListener('pointerup', commitSliderDraft);
    return () => document.removeEventListener('pointerup', commitSliderDraft);
  }, [sliderDraft != null, onFilterMinChange, onFilterMaxChange]);

  const scopedSearch = useMemo(
    () =>
      parseScopedSearchQuery(
        activeQuery,
        browseCategories,
        activeShopCategorySlug,
      ),
    [activeQuery, browseCategories, activeShopCategorySlug],
  );
  const activeSubcategory = scopedSearch.subcategory;
  const breadcrumbSearchLabel = scopedSearch.userQuery.trim();
  const hasCategoryScope = Boolean(activeShopCategorySlug);
  const isSearchOnlyBreadcrumb =
    !hasCategoryScope && breadcrumbSearchLabel !== '';
  const categoryFilterOpen =
    openId === 'category-breadcrumb' || openId === 'category';
  const categoryDropdownOpen = openId === 'category-breadcrumb';
  const categoryPillOpen = openId === 'category';

  useEffect(() => {
    if (!categoryFilterOpen) {
      setExpandedCategorySlugs(new Set());
    }
  }, [categoryFilterOpen]);

  useLayoutEffect(() => {
    if (!categoryFilterOpen) {
      return;
    }
    if (activeShopCategorySlug && activeSubcategory?.search) {
      setExpandedCategorySlugs((prev) => {
        if (prev.has(activeShopCategorySlug)) {
          return prev;
        }
        return new Set([...prev, activeShopCategorySlug]);
      });
    }
  }, [categoryFilterOpen, activeShopCategorySlug, activeSubcategory?.search]);

  useLayoutEffect(() => {
    if (!categoryFilterOpen || !activeShopCategorySlug) {
      return;
    }
    scrollPanelToElement(categoryPanelRef.current, scrollTargetRef.current);
  }, [
    categoryFilterOpen,
    activeShopCategorySlug,
    activeSubcategory?.search,
    browseCategories.length,
    expandedCategorySlugs,
  ]);

  useEffect(() => {
    function handlePointerDown(e) {
      if (!rootRef.current?.contains(e.target)) {
        setOpenId(null);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, []);

  const hasDomain = domainMax > domainMin || (domainMin === domainMax && domainMin > 0);
  const safeMin = hasDomain ? domainMin : 0;
  const safeMax = hasDomain ? Math.max(domainMax, domainMin) : 1;
  const step =
    safeMax - safeMin > 50
      ? 1
      : safeMax - safeMin > 10
        ? 0.5
        : 0.01;

  const displayMin = sliderDraft?.min ?? filterMin;
  const displayMax = sliderDraft?.max ?? filterMax;

  const priceActive =
    hasDomain &&
    (roundMoney(displayMin) > roundMoney(safeMin) ||
      roundMoney(displayMax) < roundMoney(safeMax));

  const retailerActive = selectedRetailers.size > 0;
  const categoryActive = Boolean(activeShopCategorySlug);

  const retailerGroups = useMemo(
    () => groupRetailersByCategory(retailers, browseCategories),
    [retailers, browseCategories],
  );

  const activeCategory = browseCategories.find(
    (c) => c.slug === activeShopCategorySlug,
  );
  const activeCategoryName = activeCategory?.name;
  const isPriceCollectionScope = activeCategory?.priceCollection === true;
  const dropdownCategories = useMemo(
    () => browseCategoriesForDropdown(browseCategories, activeShopCategorySlug),
    [browseCategories, activeShopCategorySlug],
  );

  useEffect(() => {
    if (isPriceCollectionScope && openId === 'price') {
      setOpenId(null);
    }
  }, [isPriceCollectionScope, openId]);

  function closeCategoryFilter() {
    setOpenId(null);
  }

  function toggleCategoryBreadcrumb() {
    setOpenId((v) => (v === 'category-breadcrumb' ? null : 'category-breadcrumb'));
  }

  function toggleCategoryPill() {
    setOpenId((v) => (v === 'category' ? null : 'category'));
  }

  function clearCategoryFilter() {
    closeCategoryFilter();
    (onShopCategoryFilterChange ?? onShopCategoryChange)('');
  }

  function toggleExpandedCategory(slug) {
    setExpandedCategorySlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });
  }

  function setMin(v) {
    const x = roundMoney(Number(v));
    if (Number.isNaN(x)) {
      return;
    }
    const upper = clamp(x, safeMin, safeMax);
    const nextMax = Math.max(upper, displayMax);
    setSliderDraft(null);
    onFilterMinChange(upper);
    if (nextMax !== filterMax) {
      onFilterMaxChange(nextMax);
    }
  }

  function setMax(v) {
    const x = roundMoney(Number(v));
    if (Number.isNaN(x)) {
      return;
    }
    const lower = clamp(x, safeMin, safeMax);
    const nextMin = Math.min(lower, displayMin);
    setSliderDraft(null);
    onFilterMaxChange(lower);
    if (nextMin !== filterMin) {
      onFilterMinChange(nextMin);
    }
  }

  function setSliderMin(v) {
    const next = clamp(v, safeMin, safeMax);
    let max = sliderDraft?.max ?? filterMax;
    if (next > max) {
      max = next;
    }
    setSliderDraft({ min: next, max });
  }

  function setSliderMax(v) {
    const next = clamp(v, safeMin, safeMax);
    let min = sliderDraft?.min ?? filterMin;
    if (next < min) {
      min = next;
    }
    setSliderDraft({ min, max: next });
  }

  function pillLabelPrice() {
    if (!hasDomain) {
      return 'Price';
    }
    if (!priceActive) {
      return 'Price';
    }
    return `${fmt.format(displayMin)} – ${fmt.format(displayMax)}`;
  }

  function pillLabelCategoryValue() {
    if (!activeShopCategorySlug) {
      return null;
    }
    const name = activeSubcategory ? activeSubcategory.name : activeCategoryName;
    return name || null;
  }

  function categoryPillAriaLabel() {
    const value = pillLabelCategoryValue();
    if (!value) {
      return 'Filter by category';
    }
    return `Category: ${value}`;
  }

  const breadcrumbCategoryPanelProps = {
    panelRef: categoryPanelRef,
    scrollTargetRef,
    dropdownCategories,
    activeShopCategorySlug,
    activeSubcategorySearch: activeSubcategory?.search ?? null,
    expandedCategorySlugs,
    onToggleExpanded: toggleExpandedCategory,
    onShopCategoryChange,
    onSubcategoryChange,
    onClose: closeCategoryFilter,
  };

  const filterCategoryPanelProps = {
    ...breadcrumbCategoryPanelProps,
    onShopCategoryChange: onShopCategoryFilterChange ?? onShopCategoryChange,
    onSubcategoryChange: onSubcategoryFilterChange ?? onSubcategoryChange,
  };

  const categoryPillValue = pillLabelCategoryValue();

  useLayoutEffect(() => {
    let trigger = null;
    let panel = null;

    if (openId === 'category-breadcrumb') {
      trigger = categoryBreadcrumbTriggerRef.current;
      panel = categoryPanelRef.current;
    } else if (openId === 'category') {
      trigger = categoryPillTriggerRef.current;
      panel = categoryPanelRef.current;
    } else if (openId === 'retailer') {
      trigger = retailerTriggerRef.current;
      panel = retailerPanelRef.current;
    }

    if (!trigger || !panel) {
      return undefined;
    }

    function alignOpenPanel() {
      alignFilterPanelToViewport(trigger, panel);
    }

    alignOpenPanel();

    window.addEventListener('resize', alignOpenPanel);
    window.addEventListener('scroll', alignOpenPanel, true);

    return () => {
      window.removeEventListener('resize', alignOpenPanel);
      window.removeEventListener('scroll', alignOpenPanel, true);
      resetFilterPanelPosition(panel);
    };
  }, [
    openId,
    categoryPillValue,
    retailerGroups.length,
    browseCategories.length,
    expandedCategorySlugs,
  ]);

  return (
    <div className="filter-bar" ref={rootRef}>
      {(browseCategories.length > 0 || isSearchOnlyBreadcrumb) && (
        <div className="filter-bar__category-nav">
          <nav
            className="category-breadcrumbs"
            aria-label={isSearchOnlyBreadcrumb ? 'Search' : 'Browse category'}
          >
            {isSearchOnlyBreadcrumb ? (
              <>
                <span className="category-breadcrumbs__crumb category-breadcrumbs__crumb--search-root">
                  Search
                </span>
                <BreadcrumbSep />
                <span
                  className="category-breadcrumbs__crumb category-breadcrumbs__crumb--current category-breadcrumbs__crumb--search"
                  aria-current="page"
                >
                  {breadcrumbSearchLabel}
                </span>
              </>
            ) : (
              <>
                <Link to="/" className="category-breadcrumbs__crumb category-breadcrumbs__crumb--link">
                  Home
                </Link>
                <BreadcrumbSep />
                {activeSubcategory && (
                  <>
                    <button
                      type="button"
                      className="category-breadcrumbs__crumb category-breadcrumbs__crumb--link"
                      onClick={() => onShopCategoryChange(activeShopCategorySlug)}
                    >
                      {activeCategoryName}
                    </button>
                    <BreadcrumbSep />
                  </>
                )}
                {(hasCategoryScope || !breadcrumbSearchLabel) && (
                  <div
                    className={`category-breadcrumbs__menu${categoryDropdownOpen ? ' category-breadcrumbs__menu--open' : ''}`}
                  >
                    <button
                      ref={categoryBreadcrumbTriggerRef}
                      type="button"
                      className="category-breadcrumbs__crumb category-breadcrumbs__crumb--dropdown"
                      aria-expanded={categoryDropdownOpen}
                      aria-haspopup="true"
                      aria-label={
                        hasCategoryScope
                          ? (activeSubcategory ? 'Change subcategory' : 'Change category')
                          : 'Browse categories'
                      }
                      onClick={toggleCategoryBreadcrumb}
                    >
                      <span className="category-breadcrumbs__dropdown-label">
                        {hasCategoryScope
                          ? (activeSubcategory
                            ? activeSubcategory.name
                            : activeCategoryName)
                          : 'Categories'}
                      </span>
                    </button>
                    {categoryDropdownOpen && (
                      <CategoryFilterPanel
                        {...breadcrumbCategoryPanelProps}
                        className="category-breadcrumbs__panel"
                        ariaLabel="Browse categories"
                      />
                    )}
                  </div>
                )}
                {breadcrumbSearchLabel && (
                  <>
                    <BreadcrumbSep />
                    <span
                      className="category-breadcrumbs__crumb category-breadcrumbs__crumb--current category-breadcrumbs__crumb--search"
                      aria-current="page"
                    >
                      {breadcrumbSearchLabel}
                    </span>
                  </>
                )}
              </>
            )}
          </nav>
        </div>
      )}
      <div className="filter-bar__pills">
        {!isPriceCollectionScope && (
        <div className={`filter-pill${openId === 'price' ? ' filter-pill--open' : ''}`}>
          <button
            type="button"
            className={`filter-pill__trigger${priceActive ? ' filter-pill__trigger--active' : ''}`}
            aria-expanded={openId === 'price'}
            aria-haspopup="true"
            onClick={() =>
              setOpenId((v) => (v === 'price' ? null : 'price'))
            }
          >
            {pillLabelPrice()}
            <span className="filter-pill__chevron" aria-hidden>
              ▾
            </span>
          </button>
          {openId === 'price' && !hasDomain && (
            <div className="filter-pill__panel" role="dialog" aria-label="Filter by price">
              <p className="filter-pill__empty">No priced products in this list.</p>
            </div>
          )}
          {openId === 'price' && hasDomain && (
            <div
              className="filter-pill__panel"
              role="dialog"
              aria-label="Filter by price"
            >
              <div className="filter-price">
                <div className="filter-price__inputs">
                  <label className="filter-price__field">
                    <span className="filter-price__label">Min</span>
                    <input
                      type="number"
                      className="filter-price__input"
                      min={safeMin}
                      max={safeMax}
                      step={step}
                      value={Number.isFinite(displayMin) ? displayMin : ''}
                      onChange={(e) => setMin(e.target.value)}
                    />
                  </label>
                  <span className="filter-price__dash">–</span>
                  <label className="filter-price__field">
                    <span className="filter-price__label">Max</span>
                    <input
                      type="number"
                      className="filter-price__input"
                      min={safeMin}
                      max={safeMax}
                      step={step}
                      value={Number.isFinite(displayMax) ? displayMax : ''}
                      onChange={(e) => setMax(e.target.value)}
                    />
                  </label>
                </div>
                <div className="filter-price__sliders">
                  <label className="sr-only" htmlFor="filter-price-min-range">
                    Minimum price slider
                  </label>
                  <input
                    id="filter-price-min-range"
                    type="range"
                    className="filter-price__range filter-price__range--min"
                    min={safeMin}
                    max={safeMax}
                    step={step}
                    value={clamp(displayMin, safeMin, safeMax)}
                    onChange={(e) => setSliderMin(Number(e.target.value))}
                  />
                  <label className="sr-only" htmlFor="filter-price-max-range">
                    Maximum price slider
                  </label>
                  <input
                    id="filter-price-max-range"
                    type="range"
                    className="filter-price__range filter-price__range--max"
                    min={safeMin}
                    max={safeMax}
                    step={step}
                    value={clamp(displayMax, safeMin, safeMax)}
                    onChange={(e) => setSliderMax(Number(e.target.value))}
                  />
                </div>
                <p className="filter-price__hint">
                  Range {fmt.format(safeMin)} — {fmt.format(safeMax)}
                </p>
              </div>
            </div>
          )}
        </div>
        )}

        {browseCategories.length > 0 && (
        <div className={`filter-pill filter-pill--category${categoryPillOpen ? ' filter-pill--open' : ''}`}>
          <div
            ref={categoryPillTriggerRef}
            className={`filter-pill__trigger filter-pill__trigger--split filter-pill__trigger--category${categoryActive ? ' filter-pill__trigger--active' : ''}`}
          >
            <button
              type="button"
              className="filter-pill__trigger-main"
              aria-expanded={categoryPillOpen}
              aria-haspopup="true"
              aria-label={categoryPillAriaLabel()}
              onClick={toggleCategoryPill}
            >
              <span className="filter-pill__label">Category</span>
              <span className="filter-pill__chevron" aria-hidden>
                ▾
              </span>
            </button>
            {categoryPillValue && (
              <span className="filter-pill__value">
                <span className="filter-pill__value-text">{categoryPillValue}</span>
                <button
                  type="button"
                  className="filter-pill__value-clear"
                  aria-label={`Remove ${categoryPillValue} filter`}
                  onClick={clearCategoryFilter}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </span>
            )}
          </div>
          {categoryPillOpen && (
            <CategoryFilterPanel
              {...filterCategoryPanelProps}
              showCounts={false}
              ariaLabel="Filter by category"
            />
          )}
        </div>
        )}

        {SEARCH_RETAILER_FILTER_ENABLED && (
        <div className={`filter-pill filter-pill--retailer${openId === 'retailer' ? ' filter-pill--open' : ''}`}>
          <button
            ref={retailerTriggerRef}
            type="button"
            className={`filter-pill__trigger${retailerActive ? ' filter-pill__trigger--active' : ''}`}
            aria-expanded={openId === 'retailer'}
            aria-haspopup="true"
            onClick={() => {
              setOpenId((v) => (v === 'retailer' ? null : 'retailer'));
            }}
          >
            Retailer
            {retailerActive && (
              <span className="filter-pill__badge">{selectedRetailers.size}</span>
            )}
            <span className="filter-pill__chevron" aria-hidden>
              ▾
            </span>
          </button>
          {openId === 'retailer' && (
            <div
              ref={retailerPanelRef}
              className="filter-pill__panel filter-pill__panel--scroll filter-pill__panel--retailer"
              role="dialog"
              aria-label="Filter by retailer"
            >
              {retailers.length === 0 ? (
                <p className="filter-pill__empty">No retailers in this list.</p>
              ) : (
                <ul className="filter-check-list filter-check-list--stack">
                  {retailerGroups.map((group) => (
                    <li key={group.categoryName} className="filter-check-list__group">
                      <div
                        className="filter-check-list__group-heading"
                        aria-hidden="true"
                      >
                        {group.categoryName}
                      </div>
                      <ul className="filter-check-list__sub-list">
                        {group.retailers.map((name) => (
                          <li key={name} className="filter-check-list__sub-item">
                            <label className="filter-check-list__label filter-check-list__label--sub">
                              <input
                                type="checkbox"
                                checked={selectedRetailers.has(name)}
                                onChange={() => onToggleRetailer(name)}
                              />
                              <span>{name}</span>
                            </label>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        )}

        <FilterCheckboxPill checked={saleActive} onChange={onSaleChange}>
          Sale
        </FilterCheckboxPill>

        <FilterCheckboxPill checked={onlyInStock} onChange={onOnlyInStockChange}>
          In stock
        </FilterCheckboxPill>

        <FilterViewToggle
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          className="filter-view-toggle--in-bar"
        />
      </div>
    </div>
  );
}
